import React from "react";
import { useReveal } from "../../hooks/useReveal";
import { Ornament, ZigguratDivider } from "../Ornaments";

const pillars = [
  {
    numeral: "I",
    title: "Map the Grid",
    body: "Inner and outer. Most people spend their lives guessing at who they are — reacting from patterns they never chose. Intentional movement makes the grid visible: where you hold, where you collapse, where you compensate, and where you're capable of more than you've accessed.",
  },
  {
    numeral: "II",
    title: "Cultivate the Signature",
    body: "Every person carries a unique pattern of embodied organization shaped by history, biology, and choice. Through playful exploration of movement and equilibrium, that signature becomes coherent — no longer a record of what happened to you, but a living expression of who you are becoming.",
  },
  {
    numeral: "III",
    title: "Self-Program With Precision",
    body: "Mentally, physically, energetically. Not guessing. Knowing. The integration of nervous system science, psychological depth, and the ancient understanding of the body as a tuned instrument — converging into a single, conscious practice of becoming.",
  },
];

const PillarCard = ({ numeral, title, body, index }) => {
  const rv = useReveal();
  return (
    <article
      ref={rv.ref}
      className={`card-gold-top reveal ${rv.inView ? "in-view" : ""}`}
      style={{ transitionDelay: `${index * 120}ms` }}
      data-testid={`pillar-card-${index}`}
    >
      <div style={{ display: "flex", alignItems: "baseline", gap: "1rem", marginBottom: "1.25rem" }}>
        <span className="numeral" data-testid={`pillar-numeral-${index}`}>
          {numeral}
        </span>
        <span style={{ flex: 1, height: 1, background: "linear-gradient(90deg, var(--gold), transparent)" }} />
      </div>
      <h3
        className="font-display"
        style={{
          color: "var(--text)",
          fontSize: "1.35rem",
          letterSpacing: "0.02em",
          marginBottom: "1rem",
          lineHeight: 1.25,
        }}
      >
        {title}
      </h3>
      <p className="font-body" style={{ color: "var(--text-muted)", fontSize: "0.97rem", lineHeight: 1.8 }}>
        {body}
      </p>
    </article>
  );
};

export const Pillars = () => {
  const head = useReveal();
  return (
    <section id="method" className="section" data-testid="pillars-section" style={{ position: "relative" }}>
      <div className="violet-glow" style={{ width: 700, height: 700, top: -200, left: "30%", opacity: 0.6 }} />
      <div className="section-inner">
        <div
          ref={head.ref}
          className={`reveal ${head.inView ? "in-view" : ""}`}
          style={{ textAlign: "center", maxWidth: 780, margin: "0 auto 3.5rem" }}
        >
          <div style={{ display: "flex", justifyContent: "center" }}>
            <Ornament centered />
          </div>
          <p className="font-heading" style={{ color: "var(--gold)", fontSize: "0.7rem", letterSpacing: "0.4em", marginBottom: "1rem" }}>
            The Method
          </p>
          <h2 className="section-title" data-testid="pillars-title">
            Three Threads. One Coherence.
          </h2>
          <div style={{ display: "flex", justifyContent: "center", marginTop: "1.25rem" }}>
            <ZigguratDivider width={220} height={24} />
          </div>
          <p className="italic-quote" data-testid="pillars-riff-quote" style={{ marginTop: "1.5rem", color: "var(--text-muted)", fontSize: "1rem", lineHeight: 1.8 }}>
            &ldquo;Finding a song — that&rsquo;s the same pattern as finding and building your signature.&rdquo;
          </p>
        </div>

        <div className="three-col">
          {pillars.map((p, i) => (
            <PillarCard key={p.numeral} numeral={p.numeral} title={p.title} body={p.body} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
